import {
  CreateBasicPermissionItem,
  BasicPermissionItem,
} from './PermissionItem'

type ActionType =
  | 'Create'
  | 'View'
  | 'Edit'
  | 'Remove'
  | 'Remove & Restore'
  | 'Comment on'
  | 'Route'
  | 'Revise'
  | 'Review'
  | 'Close'
  | 'Sync'
  | 'Import'
  | 'Export'
export interface SimpleOptions<
  ID extends Lowercase<string>,
  Action extends ActionType,
  ObjectName extends string,
  PageID extends Lowercase<string>,
  PageName extends string,
  PagePathname extends string,
  ModuleID extends Lowercase<string>,
  ModuleName extends string,
  ModulePathname extends string
> extends Pick<
    CreateBasicPermissionItem<
      ID,
      string,
      PageID,
      PageName,
      PagePathname,
      ModuleID,
      ModuleName,
      ModulePathname
    >,
    'id' | 'page'
  > {
  /**
   * the human readable version of the object name in title case
   *
   * this shouldn't include any module prefixes like CAS, FDC, etc.
   *
   * @example
   * const object1 = 'Action'
   * const object2 = 'Field Design Change'
   * const object3 = 'Concurrence Group'
   */
  object: ObjectName
  /**
   * if {@link object} can't be pluralized by adding an `s` to the end, supply
   * the fully pluralized version here
   *
   * @example
   * const object = 'Concrete Break Data'
   * const plural = 'Concrete Break Data'
   */
  plural?: string
  /**
   * which simple action to perform with this permission
   *
   * this helps resolve the name and description
   * @example
   * const name = `${action} ${object}`
   * const action = `${action} ${plural(options)}`
   * const description = `Permits a user to ${action.toLowerCase()}`
   * @see {@link Action}
   */
  action: Action
}

/**
 * a simplified wrapper to handle common, reoccurring, permission types
 *
 * @example
 * new BasicPermissionItem({
 *   id,
 *   page,
 *   name: `${action} ${rest.object}`,
 *   action: `${action} ${plural(rest)}`,
 * })
 *
 * @param options {@link SimpleOptions}
 * @see {@link BasicPermissionItem}
 * @see {@link SimpleOptions.action|actions}
 * @see {@link CreateViewEditRemove}
 * @see {@link CreateViewEditSoftRemoveRestore}
 */
export function Simple<
  ID extends Lowercase<string>,
  Action extends ActionType,
  ObjectName extends string,
  PageID extends Lowercase<string>,
  PageName extends string,
  PagePathname extends string,
  ModuleID extends Lowercase<string>,
  ModuleName extends string,
  ModulePathname extends string
>({
  id,
  page,
  action,
  ...rest
}: SimpleOptions<
  ID,
  Action,
  ObjectName,
  PageID,
  PageName,
  PagePathname,
  ModuleID,
  ModuleName,
  ModulePathname
>) {
  return new BasicPermissionItem({
    id,
    page,
    name: `${action} ${rest.object}` as const,
    action: `${action} ${plural(rest)}`,
    crud:
      action === 'Create'
        ? 'Create'
        : action === 'Edit'
        ? 'Edit'
        : action === 'View'
        ? 'View'
        : action === 'Remove'
        ? 'Remove'
        : action === 'Remove & Restore'
        ? 'Remove & Restore'
        : undefined,
  })
}

/**
 * a wrapper to quickly define disposition based permissions
 *
 * @example
 * new BasicPermissionItem({
 *   id,
 *   page,
 *   name: 'Give Disposition',
 *   action: `give final disposition on ${plural(rest)}`,
 * })
 *
 * @param options {@link SimpleOptions}
 * @see {@link BasicPermissionItem}
 */
export function GiveDisposition<
  ID extends Lowercase<string>,
  PageID extends Lowercase<string>,
  PageName extends string,
  PagePathname extends string,
  ModuleID extends Lowercase<string>,
  ModuleName extends string,
  ModulePathname extends string
>({
  id,
  page,
  ...rest
}: Omit<
  SimpleOptions<
    ID,
    ActionType,
    string,
    PageID,
    PageName,
    PagePathname,
    ModuleID,
    ModuleName,
    ModulePathname
  >,
  'action'
>) {
  return new BasicPermissionItem({
    id,
    page,
    name: 'Give Disposition',
    action: `give final disposition on ${plural(rest)}`,
  })
}

/**
 * a wrapper to quickly define SharePoint package number/name edit permissions
 *
 * @example
 * new BasicPermissionItem({
 *   id,
 *   page,
 *   name: 'Edit Number Name',
 *   action: `edit the package number or name of ${plural(rest)}`,
 * })
 *
 * @param options {@link SimpleOptions}
 * @see {@link BasicPermissionItem}
 */
export function EditPackageNumberName<
  ID extends Lowercase<string>,
  PageID extends Lowercase<string>,
  PageName extends string,
  PagePathname extends string,
  ModuleID extends Lowercase<string>,
  ModuleName extends string,
  ModulePathname extends string
>({
  id,
  page,
  ...rest
}: Omit<
  SimpleOptions<
    ID,
    ActionType,
    string,
    PageID,
    PageName,
    PagePathname,
    ModuleID,
    ModuleName,
    ModulePathname
  >,
  'action'
>) {
  return new BasicPermissionItem({
    id,
    page,
    name: 'Edit Number Name',
    action: `edit the package number or name of ${plural(rest)}`,
  })
}

/**
 * a wrapper to quickly define reversal permissions
 *
 * @example
 * new BasicPermissionItem({
 *   id,
 *   page,
 *   name: `Revert ${rest.object}`,
 *   action: `revert to previous workflow statuses on ${plural(rest)}`,
 * })
 *
 * @param options {@link SimpleOptions}
 * @see {@link BasicPermissionItem}
 */
export function Revert<
  ID extends Lowercase<string>,
  ObjectName extends string,
  PageID extends Lowercase<string>,
  PageName extends string,
  PagePathname extends string,
  ModuleID extends Lowercase<string>,
  ModuleName extends string,
  ModulePathname extends string
>({
  id,
  page,
  ...rest
}: Omit<
  SimpleOptions<
    ID,
    ActionType,
    ObjectName,
    PageID,
    PageName,
    PagePathname,
    ModuleID,
    ModuleName,
    ModulePathname
  >,
  'action'
>) {
  return new BasicPermissionItem({
    id,
    page,
    name: `Revert ${rest.object}`,
    action: `revert to previous workflow statuses on ${plural(rest)}`,
  })
}

/**
 * a wrapper to quickly define unlock permissions
 *
 * @example
 * new BasicPermissionItem({
 *   id,
 *   page,
 *   name: `Unlock ${object}`,
 *   action: `unlock ${plural({ object })}`,
 * })
 *
 * @param options
 * @see {@link BasicPermissionItem}
 * @see {@link object}
 */
export function Unlock<
  ID extends Lowercase<string>,
  ObjectName extends
    | 'Construction & Admin Submittal'
    | 'Field Design Change'
    | 'Design Submittal'
    | 'Issue'
    | 'Request For Information',
  PageID extends Lowercase<string>,
  PageName extends string,
  PagePathname extends string,
  ModuleID extends Lowercase<string>,
  ModuleName extends string,
  ModulePathname extends string
>({
  id,
  page,
  ...rest
}: Omit<
  SimpleOptions<
    ID,
    ActionType,
    string,
    PageID,
    PageName,
    PagePathname,
    ModuleID,
    ModuleName,
    ModulePathname
  >,
  'action' | 'object'
> & {
  object: ObjectName
}) {
  return new BasicPermissionItem({
    id,
    page,
    name: `Unlock ${rest.object}`,
    action: `unlock ${plural({ ...rest })}`,
  })
}

/** resolves the {@link object} into its plural form */
function plural({
  object,
  plural,
}: Pick<
  SimpleOptions<
    Lowercase<string>,
    ActionType,
    string,
    Lowercase<string>,
    string,
    string,
    Lowercase<string>,
    string,
    string
  >,
  'object' | 'plural'
>) {
  return plural ?? object + 's'
}

interface SimpleCRUDOptions<
  CreateID extends Lowercase<string>,
  ViewID extends Lowercase<string>,
  EditID extends Lowercase<string>,
  PageID extends Lowercase<string>,
  PageName extends string,
  PagePathname extends string,
  ModuleID extends Lowercase<string>,
  ModuleName extends string,
  ModulePathname extends string,
  ObjectName extends string
> extends Pick<
    SimpleOptions<
      Lowercase<string>,
      ActionType,
      ObjectName,
      PageID,
      PageName,
      PagePathname,
      ModuleID,
      ModuleName,
      ModulePathname
    >,
    'page' | 'object' | 'plural'
  > {
  /**
   * an {@link CreateBasicPermissionItem.id|id} for the create permission
   */
  create: CreateID
  /**
   * an {@link CreateBasicPermissionItem.id|id} for the view permission
   */
  view: ViewID
  /**
   * an {@link CreateBasicPermissionItem.id|id} for the edit permission
   */
  edit: EditID
}

interface RemoveRestore<ID extends string> {
  /**
   * an {@link CreateBasicPermissionItem.id|id} for the soft remove & restore permission
   */
  removeRestore: ID
}

interface Remove<ID extends string> {
  /**
   * an {@link CreateBasicPermissionItem.id|id} for the remove permission
   */
  remove: ID
}

/**
 * wraps the {@link Simple} function for create, vew, edit, and remove
 *
 * @example
 * const breakData = CreateViewEditRemove({
 *   object: 'Concrete Break Data',
 *   plural: 'concrete break data',
 *   page: CIMSPage.ConcreteBreakForm,
 *   create: 'B3BD4D1F-7709-45E4-9449-ACDFC1A105D1',
 *   view: 'F8CEC9C0-FE4C-4FE6-AF7E-D4AFE02FC0E8',
 *   edit: '7B326C2D-9809-4592-8804-BBC03D3B655F',
 *   remove: '10037B44-919D-4652-9CA7-83B2D31CDF3D',
 * })
 *
 * export const CreateConcreteBreakForm = breakData.Create
 * export const ViewConcreteBreakForm = breakData.View
 * export const EditConcreteBreakForm = breakData.Edit
 * export const RemoveConcreteBreakForm = breakData.Remove
 *
 * @param options
 * @see {@link SimpleCRUDOptions}
 * @see {@link Remove}
 */
export function CreateViewEditRemove<
  CreateID extends Lowercase<string>,
  ViewID extends Lowercase<string>,
  EditID extends Lowercase<string>,
  RemoveID extends Lowercase<string>,
  PageID extends Lowercase<string>,
  PageName extends string,
  PagePathname extends string,
  ModuleID extends Lowercase<string>,
  ModuleName extends string,
  ModulePathname extends string,
  ObjectName extends string
>({
  create,
  edit,
  remove,
  view,
  ...rest
}: SimpleCRUDOptions<
  CreateID,
  ViewID,
  EditID,
  PageID,
  PageName,
  PagePathname,
  ModuleID,
  ModuleName,
  ModulePathname,
  ObjectName
> &
  Remove<RemoveID>) {
  function s<ID extends Lowercase<string>, Action extends ActionType>(
    id: ID,
    action: Action
  ) {
    return Simple({ id, action, ...rest })
  }
  return {
    Create: s(create, 'Create'),
    View: s(view, 'View'),
    Edit: s(edit, 'Edit'),
    Remove: s(remove, 'Remove'),
  }
}

/**
 * wraps the {@link Simple} function for create, view, edit, soft remove, and restore
 *
 * @example
 * const groups = CreateViewEditSoftRemoveRestore({
 *   page: CIMSPage.FDCApprovalGroups,
 *   object: 'Approval Group',
 *   view: 'AFADA74D-6CB8-4D1C-92BD-6C7FF85A54A9',
 *   create: 'E49E40F7-6FFF-421C-8071-991C711A789D',
 *   edit: '11075B20-60E7-4B24-94C9-4F67058959B6',
 *   removeRestore: '1B6C4A73-685D-4D57-8B8B-9648E3F779C1',
 * })
 *
 * export const ViewFDCApprovalGroup = groups.View
 * export const CreateFDCApprovalGroup = groups.Create
 * export const EditFDCApprovalGroup = groups.Edit
 * export const RemoveRestoreFDCApprovalGroup = groups.RemoveRestore
 *
 * @param options
 * @see {@link SimpleCRUDOptions}
 * @see {@link RemoveRestore}
 */
export function CreateViewEditSoftRemoveRestore<
  CreateID extends Lowercase<string>,
  ViewID extends Lowercase<string>,
  EditID extends Lowercase<string>,
  RemoveRestoreID extends Lowercase<string>,
  PageID extends Lowercase<string>,
  PageName extends string,
  PagePathname extends string,
  ModuleID extends Lowercase<string>,
  ModuleName extends string,
  ModulePathname extends string,
  ObjectName extends string
>({
  create,
  edit,
  view,
  removeRestore,
  ...rest
}: SimpleCRUDOptions<
  CreateID,
  ViewID,
  EditID,
  PageID,
  PageName,
  PagePathname,
  ModuleID,
  ModuleName,
  ModulePathname,
  ObjectName
> &
  RemoveRestore<RemoveRestoreID>) {
  function s<ID extends Lowercase<string>, Action extends ActionType>(
    id: ID,
    action: Action
  ) {
    return Simple({ id, action, ...rest })
  }
  return {
    Create: s(create, 'Create'),
    View: s(view, 'View'),
    Edit: s(edit, 'Edit'),
    RemoveRestore: s(removeRestore, 'Remove & Restore'),
  }
}

type CreateViewEditOptions<
  CreateID extends Lowercase<string>,
  ViewID extends Lowercase<string>,
  EditID extends Lowercase<string>,
  PageID extends Lowercase<string>,
  PageName extends string,
  PagePathname extends string,
  ModuleID extends Lowercase<string>,
  ModuleName extends string,
  ModulePathname extends string
> = Omit<
  SimpleCRUDOptions<
    CreateID,
    ViewID,
    EditID,
    PageID,
    PageName,
    PagePathname,
    ModuleID,
    ModuleName,
    ModulePathname,
    string
  >,
  'object' | 'plural'
>

/**
 * wraps {@link CreateViewEditSoftRemoveRestore} for types
 *
 * @example
 * CreateViewEditRemove({ object: 'Quick Link', ...options })
 *
 * @param options
 * @see {@link CreateViewEditOptions}
 * @see {@link RemoveRestore}
 */
export function Types<
  CreateID extends Lowercase<string>,
  ViewID extends Lowercase<string>,
  EditID extends Lowercase<string>,
  RemoveRestoreID extends Lowercase<string>,
  PageID extends Lowercase<string>,
  PageName extends string,
  PagePathname extends string,
  ModuleID extends Lowercase<string>,
  ModuleName extends string,
  ModulePathname extends string
>(
  options: CreateViewEditOptions<
    CreateID,
    ViewID,
    EditID,
    PageID,
    PageName,
    PagePathname,
    ModuleID,
    ModuleName,
    ModulePathname
  > &
    RemoveRestore<RemoveRestoreID>
) {
  return CreateViewEditSoftRemoveRestore({ object: 'Type', ...options })
}
/**
 * wraps {@link CreateViewEditSoftRemoveRestore} for quick links
 *
 * @example
 * CreateViewEditRemove({ object: 'Quick Link', ...options })
 *
 * @param options
 * @see {@link CreateViewEditOptions}
 * @see {@link RemoveRestore}
 */
export function QuickLinks<
  CreateID extends Lowercase<string>,
  ViewID extends Lowercase<string>,
  EditID extends Lowercase<string>,
  RemoveRestoreID extends Lowercase<string>,
  PageID extends Lowercase<string>,
  PageName extends string,
  PagePathname extends string,
  ModuleID extends Lowercase<string>,
  ModuleName extends string,
  ModulePathname extends string
>(
  options: CreateViewEditOptions<
    CreateID,
    ViewID,
    EditID,
    PageID,
    PageName,
    PagePathname,
    ModuleID,
    ModuleName,
    ModulePathname
  > &
    RemoveRestore<RemoveRestoreID>
) {
  return CreateViewEditSoftRemoveRestore({ object: 'Quick Link', ...options })
}

/**
 * wraps {@link CreateViewEditRemove} for disposition types
 *
 * @example
 * CreateViewEditRemove({ object: 'Disposition Type', ...options })
 *
 * @param options
 * @see {@link CreateViewEditOptions}
 * @see {@link RemoveRestore}
 */
export function DispositionTypes<
  CreateID extends Lowercase<string>,
  ViewID extends Lowercase<string>,
  EditID extends Lowercase<string>,
  RemoveRestoreID extends Lowercase<string>,
  PageID extends Lowercase<string>,
  PageName extends string,
  PagePathname extends string,
  ModuleID extends Lowercase<string>,
  ModuleName extends string,
  ModulePathname extends string
>(
  options: CreateViewEditOptions<
    CreateID,
    ViewID,
    EditID,
    PageID,
    PageName,
    PagePathname,
    ModuleID,
    ModuleName,
    ModulePathname
  > &
    RemoveRestore<RemoveRestoreID>
) {
  return CreateViewEditSoftRemoveRestore({
    object: 'Disposition Type',
    ...options,
  })
}

interface NotificationsOptions<
  EditID extends Lowercase<string>,
  ViewID extends Lowercase<string>,
  PageID extends Lowercase<string>,
  PageName extends string,
  PagePathname extends string,
  ModuleID extends Lowercase<string>,
  ModuleName extends string,
  ModulePathname extends string
> extends Pick<
    SimpleOptions<
      Lowercase<string>,
      ActionType,
      string,
      PageID,
      PageName,
      PagePathname,
      ModuleID,
      ModuleName,
      ModulePathname
    >,
    'page'
  > {
  /**
   * an {@link CreateBasicPermissionItem.id|id} for the view permission
   */
  view: ViewID
  /**
   * an {@link CreateBasicPermissionItem.id|id} for the edit permission
   */
  edit: EditID
}

/**
 * wraps {@link Simple} permissions for Notifications
 *
 * @param options {@link NotificationsOptions}
 */
export function Notifications<
  EditID extends Lowercase<string>,
  ViewID extends Lowercase<string>,
  PageID extends Lowercase<string>,
  PageName extends string,
  PagePathname extends string,
  ModuleID extends Lowercase<string>,
  ModuleName extends string,
  ModulePathname extends string
>({
  edit,
  view,
  page,
}: NotificationsOptions<
  EditID,
  ViewID,
  PageID,
  PageName,
  PagePathname,
  ModuleID,
  ModuleName,
  ModulePathname
>) {
  const object = 'Notification'
  return {
    Edit: Simple({ id: edit, action: 'Edit', object, page }),
    View: Simple({ id: view, action: 'View', object, page }),
  }
}
